import { useEffect, useMemo, useState } from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useDispatch, useSelector } from 'react-redux';
import { PAUSE_RESUME_STATUSES } from 'constants/resumePause';
import { updateStopResumeOrders } from 'redux/actions/providers';
import useCountDownForProvider from './useCountDownForProvider';
import Preloader from 'components/Preloader';

function ResumePauseButtonForProvider({ provider, disabled }) {
  const dispatch = useDispatch();
  const [showSpinner, setShowSpinner] = useState(true);
  const { stopResumeOrders } = useSelector(({ providers }) => providers);
  const { isBrandLoading } = useSelector(({ business }) => business);
  const { statuses } = stopResumeOrders;
  const currentProviderStatuses = statuses?.details?.find(
    (item) => item?.provider === provider
  );

  const updateProviderStatus = (statuses, provider) => {
    if (!statuses?.details) return statuses;

    const updatedDetails = statuses.details.map((item) => {
      if (item?.provider === provider) {
        return {
          ...item,
          provider_status: PAUSE_RESUME_STATUSES.RECEIVING, // Correct key-value pair
          paused_until: null,
          immutable_until: null,
        };
      }
      return item;
    });

    return {
      ...statuses,
      details: updatedDetails,
    };
  };

  const { duration } = useCountDownForProvider(currentProviderStatuses, () => {
    const updatedDetails = updateProviderStatus(statuses, provider);
    return dispatch(
      updateStopResumeOrders({
        ...stopResumeOrders,
        data: {
          ...stopResumeOrders.data,
          action: PAUSE_RESUME_STATUSES.RECEIVING,
        },
        statuses: {
          ...stopResumeOrders.statuses,
          details: updatedDetails.details,
          paused_until: '',
          status: PAUSE_RESUME_STATUSES.RECEIVING,
        },
      })
    );
  });

  useEffect(() => {
    if (!showSpinner) {
      setShowSpinner(true);
    }
  }, [stopResumeOrders.loading]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(false);
    }, 1200);
    return () => clearTimeout(timer);
  }, [stopResumeOrders]);

  const iconStyle = useMemo(() => {
    if (
      currentProviderStatuses?.provider_status ===
      PAUSE_RESUME_STATUSES.RECEIVING
    ) {
      return { backgroundColor: '#11AF22' };
    }

    if (
      currentProviderStatuses?.provider_status === PAUSE_RESUME_STATUSES.BUSY
    ) {
      return {
        backgroundColor: '#F2AE48',
      };
    }

    return {
      backgroundColor: 'red',
    };
  }, [currentProviderStatuses?.provider_status]);

  const handleShowResumePause = () => {
    if (disabled) return;
    dispatch(updateStopResumeOrders({ showModal: true, provider: provider }));
  };

  return (
    <>
      <Tooltip
        title={disabled ? 'Provider does not support status updates' : ''}
        placement="top"
      >
        <span>
          <LoadingButton
            id="resumePauseButton"
            size="small"
            className="mr-2"
            onClick={handleShowResumePause}
            variant="outlined"
            loading={false}
            startIcon={
              !showSpinner &&
              !isBrandLoading &&
              !stopResumeOrders?.loading &&
              !disabled && (
                <Box
                  sx={{
                    ...iconStyle,
                    mr: 1,
                    width: 12,
                    height: 12,
                    borderRadius: 90,
                  }}
                />
              )
            }
            sx={{
              color: 'black',
              textTransform: 'none',
              mb: 2,
              height: '40px',
              width: '240px',
              borderColor: 'rgba(0, 0, 0, 0.23)',
            }}
            disabled={disabled}
          >
            {stopResumeOrders?.loading || isBrandLoading || showSpinner ? (
              <Preloader />
            ) : (
              <Typography sx={{ display: 'flex', alignItems: 'center' }}>
                {currentProviderStatuses?.provider_status ===
                  PAUSE_RESUME_STATUSES.NA ||
                currentProviderStatuses?.provider_status ===
                  PAUSE_RESUME_STATUSES.UNKNOWN
                  ? currentProviderStatuses?.provider_status?.toUpperCase()
                  : duration?.toUpperCase()}
              </Typography>
            )}
          </LoadingButton>
        </span>
      </Tooltip>
    </>
  );
}
export default ResumePauseButtonForProvider;
